import { Context } from "@nuxt/types";

import SearchServiceApi from "@/store/search/-api";
import { SEARCH_DEFAULT_PAGE_SIZE, SEARCH_MAX_QUERY_LENGTH } from "@/utils/constants";
import logger, { logLevel } from "@/utils/logger";

const prepareQuery = (v: string) => v.trim().substring(0, SEARCH_MAX_QUERY_LENGTH);

export default class SearchService {
  axios: Context["$axios"];
  auth: Context["$auth"];
  ltkSearch: SearchServiceApi;

  constructor({ $config, $axios, $auth }: Context) {
    this.axios = $axios;
    this.auth = $auth;

    this.ltkSearch = new SearchServiceApi({ $auth, $axios, $config });
  }

  /**
   * Search Index
   */

  async search<T extends SearchProps>({ rawQuery, type, offset, hitsPerPage }: T) {
    const searchProps: SearchManyProps = {
      rawQuery,
      indexMap: [{ type, offset, hitsPerPage }],
    };
    const results = await this.searchMany(searchProps);

    return results.results[0] as LtkSearchResponse<T["type"]>;
  }

  /**
   * Searches Many indexes with the same query
   */
  async searchMany<T extends SearchManyProps>({ rawQuery, indexMap }: T) {
    const query = prepareQuery(rawQuery);
    const searchArray: PostSearch[] = indexMap.map(({ type, hitsPerPage = SEARCH_DEFAULT_PAGE_SIZE, offset = 0 }) => {
      return {
        type,
        search: {
          params: {
            query,
            // Round with potential for skipping a page.
            // Worst case scenario is erroneously repeating a page and having duplicate IDs
            page: Math.ceil(offset / hitsPerPage),
            limit: hitsPerPage,
            analytics: ["platform:web"],
          },
        },
      };
    });

    try {
      const results = await this.ltkSearch.search(searchArray);

      return results as { results: LtkSearchResponse<QueryableIndex>[] };
    } catch (error) {
      logger(logLevel.ERROR, "Error Searching", {
        error,
        query,
        rawQuery,
        indexMap,
      });

      throw error;
    }
  }

  /**
   * Search Insights Feedback Loop
   */
  resultClick({
    type,
    event,
    index,
    objectId,
    position,
    query,
    queryID,
    rootReferrer,
    searchExperience,
  }: SearchClickEvent) {
    return this.ltkSearch.analyticsClick({
      type,
      event,
      index,
      objectId,
      position,
      query,
      queryID,
      rootReferrer,
      searchExperience,
    });
  }
}
